import type { ReactNode } from 'react';
import { getModule, getSetting } from '@onepos/shared';
import { STATUS, auditLabel, fmtDate, fmtDateTime, fmtMoney, monthsText } from '../lib/format';
import type { TenantStatus } from '../lib/types';
import { settingValueText } from '../lib/settings';
import type { AuditEntry } from '../lib/types';

/** Jurnal yozuvidagi qisqa tafsilot (sabab, qiymat va h.k.) */
function detailText(e: AuditEntry): string {
  const d = e.details ?? {};
  const parts: string[] = [];
  const pick = (k: string) => (typeof d[k] === 'string' || typeof d[k] === 'number' ? String(d[k]) : null);
  const st = (k: string) => STATUS[pick(k) as TenantStatus]?.label ?? pick(k) ?? '?';
  if (e.action === 'tenant.status') parts.push(`${st('from')} → ${st('to')}${d.auto === true ? ' (avtomatik: muddat)' : ''}`);
  if (e.action === 'billing.pay' || e.action === 'billing.void') {
    const amount = typeof d.amount === 'number' ? fmtMoney(d.amount) : '';
    const months = typeof d.months === 'number' ? monthsText(d.months) : '';
    parts.push([amount, months].filter(Boolean).join(' · '));
    if (e.action === 'billing.pay' && typeof d.to === 'string') parts.push(`muddat ${fmtDate(d.to)} gacha`);
    const note = pick('note');
    if (note) parts.push(note);
  }
  if (e.action === 'billing.auto') {
    const to = d.to as { auto_suspend?: boolean; grace_days?: number } | undefined;
    if (to) parts.push(`${to.auto_suspend ? 'yoqildi' : 'o‘chirildi'} · muhlat ${to.grace_days ?? '?'} kun`);
  }
  if (e.action === 'plan.price') {
    const to = d.to as { monthly?: number | null } | undefined;
    parts.push(`${pick('plan') ?? ''}: ${to?.monthly == null ? 'narx yo‘q' : `${fmtMoney(to.monthly)} / oy`}`);
  }
  if (e.action === 'tenant.plan') parts.push(`${pick('from') ?? '?'} → ${pick('to') ?? '?'}`);
  if (e.action === 'module.set') {
    const key = pick('key') ?? '';
    parts.push(`${getModule(key)?.label ?? key}: ${d.enabled ? 'yoqildi' : 'o‘chirildi'}${d.locked ? ' (qulf)' : ''}`);
  }
  if (e.action === 'setting.set') {
    const key = pick('key') ?? '';
    const def = getSetting(key);
    parts.push(def ? `${def.label}: ${settingValueText(def, d.value)}` : `${key} = ${JSON.stringify(d.value)}`);
  }
  if (e.action === 'device.code' || e.action === 'device.disable' || e.action === 'device.enable') {
    const n = pick('name');
    if (n) parts.push(n);
  }
  if (e.action === 'tenant.create') {
    const t = pick('template');
    if (t) parts.push(`shablon: ${t}`);
  }
  if (e.action === 'support.start') parts.push(`${pick('mode') === 'write' ? 'sozlash' : 'ko‘rish'}`);
  const reason = pick('reason');
  if (reason) parts.push(`sabab: ${reason}`);
  return parts.join(' · ');
}

export function AuditList({
  items,
  renderTenant,
}: {
  items: readonly AuditEntry[];
  renderTenant?: (e: AuditEntry) => ReactNode;
}) {
  return (
    <ol className="cn-audit">
      {items.map((e) => {
        const detail = detailText(e);
        return (
          <li key={e.id}>
            <span className="cn-audit__time op-num">{fmtDateTime(e.created_at)}</span>
            <span className="cn-audit__what">
              <strong>{auditLabel(e.action)}</strong>
              {renderTenant && <span className="cn-audit__tenant">{renderTenant(e)}</span>}
              {detail && <span className="cn-audit__detail">{detail}</span>}
            </span>
            <span className="cn-audit__who">{e.admin_name ?? 'Tizim'}</span>
          </li>
        );
      })}
    </ol>
  );
}
